/**
 * Convertit une URL d'image Supabase Storage publique en URL "render" avec
 * redimensionnement et compression, pour réduire drastiquement l'egress
 * (le bucket sert aujourd'hui les WebP en 452px même pour les vignettes 80px).
 *
 * Format URL d'entrée attendu :
 *   https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path>
 *
 * Format URL de sortie :
 *   https://<ref>.supabase.co/storage/v1/render/image/public/<bucket>/<path>?width=W&quality=Q&resize=contain
 *
 * Si l'URL n'est pas une URL Supabase Storage publique (ex: Google Drive,
 * CDN externe), on la renvoie telle quelle.
 */

const OBJECT_PUBLIC = "/storage/v1/object/public/";
const RENDER_PUBLIC = "/storage/v1/render/image/public/";

export type FrameImageSize =
  | "thumb"   // 96-120px (panier, mini-cards)
  | "card"   // 240-360px (grilles catalogue)
  | "detail" // 600-800px (page produit, lightbox preview)
  | "full";  // pas de transform, image originale

const SIZE_MAP: Record<Exclude<FrameImageSize, "full">, { width: number; quality: number }> = {
  thumb: { width: 160, quality: 70 },
  card: { width: 360, quality: 72 },
  detail: { width: 720, quality: 78 },
};

export function frameImageUrl(
  url: string | null | undefined,
  size: FrameImageSize = "card",
): string | undefined {
  if (!url) return undefined;
  if (size === "full") return url;
  if (!url.includes(OBJECT_PUBLIC)) return url;

  const opts = SIZE_MAP[size];
  const rendered = url.replace(OBJECT_PUBLIC, RENDER_PUBLIC);
  const sep = rendered.includes("?") ? "&" : "?";
  return `${rendered}${sep}width=${opts.width}&quality=${opts.quality}&resize=contain`;
}

/**
 * Variante générique : transforme n'importe quelle URL publique Supabase
 * Storage (blog, montures, etc.) en URL "render" à la largeur demandée.
 * Réduit massivement l'egress (ex: cover blog PNG 1,3 Mo -> ~30 Ko).
 */
export function storageImageUrl(
  url: string | null | undefined,
  width: number,
  quality = 72,
): string | undefined {
  if (!url) return undefined;
  if (!url.includes(OBJECT_PUBLIC)) return url;
  const rendered = url.replace(OBJECT_PUBLIC, RENDER_PUBLIC);
  const sep = rendered.includes("?") ? "&" : "?";
  return `${rendered}${sep}width=${width}&quality=${quality}&resize=contain`;
}
